/**
 * A ordem "do texto" das notas — a mesma que numera os expoentes.
 *
 * Existe como módulo próprio porque a regra tem TRÊS consumidores que precisam
 * concordar caractere a caractere:
 *
 *   1. `TextWithNotes.vue`      — numeração dos expoentes no texto puro
 *   2. `htmlNoteSegments.js`    — numeração dos expoentes no texto HTML
 *   3. `useNoteStore`           — ordem da lista de notas (espelho do servidor)
 *
 * E um quarto, fora do JavaScript: `NoteRepository.ApplySortOrder`, no backend,
 * que ordena a consulta paginada. Se qualquer um divergir, a lista aponta para
 * um expoente e o texto mostra outro — sem erro nenhum, que é o modo de falha
 * caro deste projeto.
 *
 * ⚠️ Mudar a ordem aqui muda os NÚMEROS que o leitor vê. Não é só apresentação:
 * é o índice pelo qual o usuário localiza a nota no texto.
 */

/**
 * Nota sem trecho ancorado. `0–0` é a convenção do projeto — os dois
 * renderizadores descartam essas notas da numeração, e `buildHighlightedExcerpt`
 * as trata da mesma forma.
 */
export function isDetachedNote(note) {
  return (note?.textRangeStart ?? 0) === 0 && (note?.textRangeEnd ?? 0) === 0;
}

/**
 * Ordem total das notas pela posição no texto.
 *
 * A chave principal é o **início** do trecho: entre duas notas, vem antes a que
 * começa antes. Num trecho aninhado (a = 1–100 contendo b = 50–70), `a` vem
 * antes de `b`, ainda que `b` termine primeiro.
 *
 * As chaves seguintes existem para a ordem ser TOTAL, não por capricho: sem
 * desempate determinístico, a paginação do servidor pode devolver a mesma nota
 * em duas páginas ou em nenhuma, e o desempate do frontend (ordem de inserção)
 * não é reproduzível no SQL.
 *
 *   nota avulsa por último → início → fim → atualização (desc) → noteId
 *
 * A data entra antes do `noteId` porque é ela que ordena as avulsas entre si,
 * que é como ficou decidido: sem trecho, não há posição — sobra a data.
 */
export function compareNotesByTextOrder(a, b) {
  const detachedDelta = Number(isDetachedNote(a)) - Number(isDetachedNote(b));
  if (detachedDelta !== 0) return detachedDelta;

  const startDelta = (a.textRangeStart ?? 0) - (b.textRangeStart ?? 0);
  if (startDelta !== 0) return startDelta;

  const endDelta = (a.textRangeEnd ?? 0) - (b.textRangeEnd ?? 0);
  if (endDelta !== 0) return endDelta;

  // Strings ISO comparam corretamente na ordem lexicográfica.
  if (a.updatedAt > b.updatedAt) return -1;
  if (a.updatedAt < b.updatedAt) return 1;

  return (a.noteId ?? 0) - (b.noteId ?? 0);
}
